import { useState, useEffect, useRef } from "react";
import * as THREE from "three";

export default function HexagonConfigurator() {
  const [width, setWidth] = useState(() => parseInt(localStorage.getItem("width")) || 230);
  const [length, setLength] = useState(() => parseInt(localStorage.getItem("length")) || 400);
  const [withAluminiumFrame, setWithAluminiumFrame] = useState(() => {
    return localStorage.getItem("withAluminiumFrame") === "true";
  });
  const [withWireFrame, setWithWireFrame] = useState(() => {
    return localStorage.getItem("withWireFrame") === "true";
  });
  const [showFullFrame, setShowFullFrame] = useState(() => {
    return localStorage.getItem("showFullFrame") === "true";
  });

  const lightSettings = {
    "4000": {
      label: "warmweiß",
      shadowColor: "#f3ebe4"
    },
    "6500": {
      label: "kaltweiß",
      shadowColor: "#e9eef5"
    }
  };
  const [typeMode, setTypeMode] = useState(() => {
    const stored = localStorage.getItem("typeMode");
    // 🔥 Migration / Fix für alte Werte wie "none"
    if (!lightSettings[stored]) {
      localStorage.setItem("typeMode", "6500");
      return "6500";
    }
    return stored;
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  const [isMobile, setIsMobile] = useState(null);

  const canvasWrapperRef = useRef(null);
  const [scaleFactor, setScaleFactor] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("2d");
  // 3D-Kameraposition bleibt bei Konfigurationsänderungen erhalten.
  // Die Szene darf neu aufgebaut werden, ohne den Blickwinkel des Nutzers zurückzusetzen.
  const threeDCameraStateRef = useRef({ yaw: -0.55, pitch: 0.52, distance: 9.4, targetY: 1.8 });
  // Aktuell gerendertes WebGL-Canvas der 3D-Ansicht (für Download/Warenkorb).
  const threeDCanvasRef = useRef(null);
  // Merkt sich einen Warenkorb-Klick aus der 3D-Ansicht, bis 2D neu gerendert ist.
  const pendingAddToCartRef = useRef(false);

  const DISCOUNT_PERCENT = 0;
  const ENABLE_LED_FRAME_FOR_4000K = true;
  const ENABLE_LED_FRAME_FOR_6500K = true;

  const hexRadius = 46;
  const canvasRef = useRef(null);
  const [hexCount, setHexCount] = useState({ cols: 0, rows: 0 });
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalNettoPrice, setTotalNettoPrice] = useState(0);

  const [discountTotalPrice, setDiscountTotalPrice] = useState(0);
  const [discountNettoPrice, setDiscountNettoPrice] = useState(0);

  const [totalHexagons, setTotalHexagons] = useState(0);

  const [totalFrameStripes, setTotalFrameStripes] = useState(0);
  const [totalHexagonStripes, setTotalHexagonStripes] = useState(0);
  const [connectors, setConnectors] = useState({ two: 0, three: 0, twoempty: 0, threeempty: 0, cable: 0 });
  const [frameConnectors, setFrameConnectors] = useState({ angle90: 0, angle180: 0, angleT: 0 });
  const [outerSize, setOuterSize] = useState({ width: width, height: length });
  const [aluFrameParts, setAluFrameParts] = useState({ stripes: 0, angle90: 0, tConnectors: 0, crosses: 0 });
  const [wireCount, setWireCount] = useState(0);

  const buttonRef = useRef();

  // ── Warenkorb State ──────────────────────────────────────────────────────────
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem("hexCart");
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [cartIsLoading, setCartIsLoading] = useState(false);
  const [addedToCartMsg, setAddedToCartMsg] = useState(false);

  // Cart im localStorage speichern wenn er sich ändert
  useEffect(() => {
    localStorage.setItem("hexCart", JSON.stringify(cart));
  }, [cart]);

  const round2 = (value) => Math.ceil(value * 100) / 100;

  const offsetX = 100;
  const offsetY = 100;

  const kaltweiss = "6500k (kaltweiß)";
  const warmweiss = "4000k (neutralweiß)";
  // Karton-Regeln
  const MAX_STRIPES_KURZ = 58;
  const MAX_LED_STRIPES_LANG = 18;
  const MAX_ALU_STRIPES = 22;

  // Gewichte pro Stück (kg)
  const WEIGHT_STRIPE_KURZ = 10 / 56;
  const WEIGHT_LED_STRIPE_LANG = 2 / 18;
  const WEIGHT_ALU_STRIPE = 7.8 / 22;


  const calculateCartons = (item) => {
    let neededKartonA = 0;
    let neededKartonB = 0;
    let neededKartonC = 0;

    let weightKartonA = 0;
    let weightKartonB = 0;
    let weightKartonC = 0;

    if (item.showFullFrame) {
      // Karton A: Hexagon + LED-Rahmen
      const aKartonByStripes = Math.ceil(
        item.totalHexagonStripes / MAX_STRIPES_KURZ
      );

      const aKartonByLED = Math.ceil(
        item.totalFrameStripes / MAX_LED_STRIPES_LANG
      );

      neededKartonA = Math.max(aKartonByStripes, aKartonByLED);

      weightKartonA =
        item.totalHexagonStripes * WEIGHT_STRIPE_KURZ +
        item.totalFrameStripes * WEIGHT_LED_STRIPE_LANG;

    } else {
      // Karton B: Hexagon ohne LED-Rahmen
      neededKartonB = Math.ceil(
        item.totalHexagonStripes / MAX_STRIPES_KURZ
      );

      weightKartonB =
        item.totalHexagonStripes * WEIGHT_STRIPE_KURZ;
    }

    if (item.withAluminiumFrame) {
      // Karton C: Aluminiumrahmen
      neededKartonC = Math.ceil(
        item.aluFrameParts.stripes / MAX_ALU_STRIPES
      );

      weightKartonC =
        item.aluFrameParts.stripes * WEIGHT_ALU_STRIPE;
    }

    const totalWeight =
      weightKartonA + weightKartonB + weightKartonC;

    const totalCartons =
      neededKartonA + neededKartonB + neededKartonC;

    return {
      neededKartonA,
      neededKartonB,
      neededKartonC,
      weightKartonA,
      weightKartonB,
      weightKartonC,
      totalWeight,
      totalCartons
    };
  };


  const calculateCartonContents = (item) => {
    const cartons = calculateCartons(item);
    const result = [];

    const lichtfarbe = item.lichtfarbe || (
      item.typeMode === "4000" ? warmweiss : kaltweiss
    );

    const shortStripes = Math.max(
      0, Number(item.totalHexagonStripes) || 0
    );
    const ledStripes = Math.max(
      0, Number(item.totalFrameStripes) || 0
    );
    const aluStripes = Math.max(
      0, Number(item.aluFrameParts?.stripes) || 0
    );

    const addPart = (parts, name, quantity) => {
      const amount = Math.max(0, Number(quantity) || 0);
      if (amount > 0) {
        parts.push({ name, quantity: amount });
      }
    };

    // ── Karton A: Hexagon mit LED-Rahmen ──────────────
    if (item.showFullFrame) {
      let remainingShort = shortStripes;
      let remainingLED = ledStripes;

      for (let i = 1; i <= cartons.neededKartonA; i++) {
        const shortAmount = Math.min(
          remainingShort, MAX_STRIPES_KURZ
        );
        const ledAmount = Math.min(
          remainingLED, MAX_LED_STRIPES_LANG
        );

        remainingShort -= shortAmount;
        remainingLED -= ledAmount;

        const parts = [];

        addPart(parts, `Hexagon Stripe KURZ ${lichtfarbe}`, shortAmount);
        addPart(parts, `LED Stripe LANG ${lichtfarbe}`, ledAmount);

        // Zubehör einmalig in den ersten Karton A
        if (i === 1) {
          addPart(parts, "120° Connector", item.connectors?.two);
          addPart(parts, "Y-Connector", item.connectors?.three);

          addPart(parts, "Stromkabel mit Stopfen (Hexagon-Hauptanschluss)", item.connectors?.cable);
          addPart(parts, "Packung Schrauben + Dübel", item.connectors?.cable);
          addPart(parts, "Anleitung", 1);

          addPart(parts, "LED 90° Connector", item.frameConnectors?.angle90);
          addPart(parts, "LED 180° Connector", item.frameConnectors?.angle180);
          addPart(parts, "LED T-Connector", item.frameConnectors?.angleT);
          addPart(parts, "Stromkabel mit Stopfen (LED-Rahmen)", item.frameConnectors?.angleT);

          if (item.withWireFrame) {
            addPart(parts, "Drahtseil Packung (5er)", item.wireCount);
          }
        }

        result.push({
          name: `Karton A${i}`,
          type: "A",
          dimensions: "123 x 25,5 x 23,5 cm",
          parts,
          weight:
            shortAmount * WEIGHT_STRIPE_KURZ +
            ledAmount * WEIGHT_LED_STRIPE_LANG
        });
      }
    }

    // ── Karton B: Hexagon ohne LED-Rahmen ─────────────
    if (!item.showFullFrame) {
      let remainingShort = shortStripes;

      for (let i = 1; i <= cartons.neededKartonB; i++) {
        const shortAmount = Math.min(
          remainingShort, MAX_STRIPES_KURZ
        );

        remainingShort -= shortAmount;

        const parts = [];

        addPart(parts, `Hexagon Stripe KURZ ${lichtfarbe}`, shortAmount);

        // Zubehör einmalig in den ersten Karton B
        if (i === 1) {
          addPart(parts, "120° Connector", item.connectors?.two);
          addPart(parts, "Y-Connector", item.connectors?.three);

          addPart(parts, "Stromkabel mit Stopfen (Hexagon-Hauptanschluss)", item.connectors?.cable);
          addPart(parts, "Packung Schrauben + Dübel", item.connectors?.cable);
          addPart(parts, "Anleitung", 1);

          if (item.withWireFrame) {
            addPart(parts, "Drahtseil Packung (5er)", item.wireCount);
          }
        }

        result.push({
          name: `Karton B${i}`,
          type: "B",
          dimensions: "48,5 x 31,5 x 27,5 cm",
          parts,
          weight: shortAmount * WEIGHT_STRIPE_KURZ
        });
      }
    }

    // ── Karton C: Aluminiumrahmen ────────────────────
    if (item.withAluminiumFrame) {
      let remainingAlu = aluStripes;

      for (let i = 1; i <= cartons.neededKartonC; i++) {
        const aluAmount = Math.min(
          remainingAlu, MAX_ALU_STRIPES
        );

        remainingAlu -= aluAmount;

        const parts = [];

        addPart(parts, "Aluminium Stripe", aluAmount);

        // Alu-Connectoren in den ersten Karton C
        if (i === 1) {
          addPart(parts, "Alu 90° Connector", item.aluFrameParts?.angle90);
          addPart(parts, "Alu T-Connector", item.aluFrameParts?.tConnectors);
          addPart(parts, "Alu Kreuz-Connector", item.aluFrameParts?.crosses);
        }

        result.push({
          name: `Karton C${i}`,
          type: "C",
          dimensions: "130 x 30 x 20 cm",
          parts,
          weight: aluAmount * WEIGHT_ALU_STRIPE
        });
      }
    }

    return result;
  };

  
  /* Methode zur detailierten Packliste. 
    Wird verkleinert, da der Notizen-Bereich nur 5000 Zeichen maximal haben kann. */
  const formatCartonContents = (item) => {
    const boxes = calculateCartonContents(item);

    let text = `

  📦 DETAILLIERTE PACKLISTE
  ======================
  Design: ${item.title}
  Benötigte Kartons: ${boxes.length}
  `;

    boxes.forEach((box) => {
      text += `
  ----------------------
  📦 ${box.name}
  Maße: ${box.dimensions}`;

      box.parts.forEach((part) => {
        text += `${part.quantity} x ${part.name}\n`;
      });

      text += `Gewicht: ${box.weight.toFixed(2)} kg`;
    });

    const totalWeight = boxes.reduce(
      (sum, box) => sum + box.weight,
      0
    );

    text += `
  ======================
  📦 ZUSAMMENFASSUNG
  ======================
  Gesamtanzahl Kartons: ${boxes.length}
  Gesamtes Gewicht: ${totalWeight.toFixed(2)} kg
  `;

    if (boxes.length >= 5) {
      text += `
  ⚠️ HINWEIS:
Bei dieser Kartonmenge kann sich der
Versand auf einer Palette lohnen.
Bitte überprüfen.
  `;
    }

    return text.trim();
  };


  const formatPrice = (price) =>
    price.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });


  const round = (n) => Math.round(n * 1000) / 1000;

  const isHorizontal = width >= 160 && length <= 158;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const mediaQuery = window.matchMedia("(max-width: 767px)");
      setIsMobile(mediaQuery.matches); // Initial prüfen
      const handleChange = () => setIsMobile(mediaQuery.matches);
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, []);
  
  useEffect(() => {
    /*if (!isMobile && canvasWrapperRef.current) {
      
    } else {
      setScaleFactor(1); // Mobile oder kein Wrapper → 100 %
    }*/
    const wrapper = canvasWrapperRef.current;
    // Im 3D-Modus ist der 2D-Wrapper nicht gemountet. Bei Änderungen der
    // Konfiguration darf der Skalierungs-Effekt deshalb nicht auf null zugreifen.
    if (!wrapper) return;
    const availableWidth = wrapper.offsetWidth;
    const availableHeight = window.innerHeight - 70; // kompakter: weniger Reserve nach unten

    const actualWidth = width + offsetX * 2;
    const actualHeight = length + offsetY * 2;

    const scaleW = availableWidth / actualWidth;
    const scaleH = availableHeight / actualHeight;

    const newScale = Math.min(scaleW, scaleH, 1); // nie größer als 100%
    setScaleFactor(newScale);

  }, [isMobile, width, length, offsetX, offsetY, viewMode]);


  useEffect(() => {
    localStorage.setItem("width", width);
    localStorage.setItem("length", length);
    localStorage.setItem("showFullFrame", showFullFrame);
    localStorage.setItem("withAluminiumFrame", withAluminiumFrame);
    localStorage.setItem("withWireFrame", withWireFrame);
    localStorage.setItem("typeMode", typeMode);
    let outerSizeHeight = length;
    let outerSizeWidth = width;
    const { cols, rows } = calculateHexCount(width, length);
    setHexCount({ cols, rows });

    // Das Canvas existiert nur in der 2D-Ansicht. Preis-, Mengen- und
    // Geometrieberechnungen laufen auch in 3D weiter; nur das 2D-Zeichnen
    // wird übersprungen, solange das Canvas nicht gemountet ist.
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d") ?? null;
    const scale = window.devicePixelRatio || 1;

    const total = calculateHexTotal(cols, rows);
    setTotalHexagons(total);

    if (canvas && ctx) {
      ctx.fillStyle = "#111827";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawHexagons(cols, rows, total, canvas, ctx, scale);
      drawWatermark(ctx, canvas, "Floor Brothers");
    }
    let additionalPrice = 0;
    let frameCost = 0;
    if (withAluminiumFrame) {
      additionalPrice += 15;
    } else {
      setAluFrameParts({
        stripes: 0,
        angle90: 0,
        tConnectors: 0,
        crosses: 0
      });
    }
    if (withWireFrame) {
      if (withAluminiumFrame) {
        const wireFrameSets = Math.ceil(total / 10);
        setWireCount(wireFrameSets);
        frameCost += wireFrameSets * 24.99;
      } else {
        const wireFramesProHex = 1.785;
        const wireFramesTotal = total * wireFramesProHex;
        const wireFrameSets = Math.ceil(wireFramesTotal / 5);
        setWireCount(wireFrameSets);
        frameCost += wireFrameSets * 24.99;
      }

    }

    let basePrice = total * (27 + additionalPrice) + 50;

    const frameStabPreis = 10;
    
    if (showFullFrame) {
      let total180Connectors = 0;
      let total90Connectors = 0;
      let totalTConnectors = 0;

      const hexWidth = 2 * hexRadius;
      const hexHeight = Math.sqrt(3) * hexRadius;
      const horizSpacing = isHorizontal ? hexHeight : 0.75 * hexWidth;
      const vertSpacing = isHorizontal ? hexWidth : hexHeight;

      const colsForHex = Math.floor(width / horizSpacing);
      const rowsForHex = Math.floor(length / vertSpacing);

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

      for (let col = 0; col < colsForHex; col++) {
        for (let row = 0; row < rowsForHex; row++) {
          if (col % 2 === 1 && row === rowsForHex - 1) continue;
          const x = col * horizSpacing + hexRadius + offsetX;
          const y = (col % 2 === 0)
            ? row * vertSpacing + hexHeight / 2 + offsetY
            : row * vertSpacing + hexHeight + offsetY;

          if (x + hexRadius > width + offsetX || y + hexHeight / 2 > length + offsetY) continue;

          minX = Math.min(minX, x - hexRadius);
          maxX = Math.max(maxX, x + hexRadius);
          minY = Math.min(minY, y - hexHeight / 2);
          maxY = Math.max(maxY, y + hexHeight / 2);
        }
      }

      const hexAreaWidth = maxX - minX;
      let hexAreaHeight = maxY - minY;

      const frameStabLength = 120;
      const outerWidth = Math.ceil(hexAreaWidth / frameStabLength) * frameStabLength;
      
      //const { colsHex, rowsHex } = calculateHexCount(width, length);
      hexAreaHeight = Math.ceil(hexAreaHeight);
      const division = rows / 3;
      // Nur wenn division eine ganze Zahl ist, addiere diesen Wert
      if (Number.isInteger(division)) {
        hexAreaHeight += division;
      }
      let result = Math.ceil(hexAreaHeight) / frameStabLength;
      const outerHeight = Math.ceil(result) * frameStabLength;
      outerSizeWidth = outerWidth;
      outerSizeHeight = outerHeight;
      setOuterSize({ width: outerWidth, height: outerHeight });

      const horizontalStabs = Math.ceil(hexAreaWidth / frameStabLength);
      const verticalStabs = Math.ceil(result);
      const totFrameStr = (horizontalStabs + verticalStabs) * 2;
      setTotalFrameStripes(totFrameStr);

      total90Connectors = 3;
      total180Connectors = ((horizontalStabs * 2) - 2) + ((verticalStabs * 2) - 2);
      totalTConnectors = 1;
      setFrameConnectors({
        angle90: total90Connectors,
        angle180: total180Connectors,
        angleT: totalTConnectors
      });
      let hexFullFrameCost = totFrameStr * frameStabPreis;
      if (typeMode == "4000") {
        hexFullFrameCost *= 1.1;
      }
      frameCost += hexFullFrameCost;
    } else {
      setTotalFrameStripes(0);
      setFrameConnectors({
        angle90: 0,
        angle180: 0,
        angleT: 0
      });
      setOuterSize({ width, height: length });
    }

    let totPrice = basePrice + frameCost;
    if (typeMode == "dimmable") {
      totPrice *= 1.1;
    } else if (typeMode == "rgb") {
      totPrice *= 1.5;
    }
        
    // Fixpreis bei bestimmter Größe (3x5)
    if (cols === 3 && rows === 5) {
      if(typeMode == "6500") {
        if (withAluminiumFrame && showFullFrame) {
          totPrice = 599;
          //748
        } else if (withAluminiumFrame && !showFullFrame) {
          totPrice = 519;
          //679
        } else if (!withAluminiumFrame && showFullFrame) {
          totPrice = 449;
          //549
        } else {
          totPrice = 369;
          //449
        }
      } else if ("4000") {
        if (withAluminiumFrame && showFullFrame) {
          totPrice = 599;
          //748
        } else if (withAluminiumFrame && !showFullFrame) {
          totPrice = 519;
          //679
        } else if (!withAluminiumFrame && showFullFrame) {
          totPrice = 449;
          //549
        } else {
          totPrice = 369;
          //449
        }
      }
      if (withWireFrame) {
        if (withAluminiumFrame) {
          const wireFrameSets = Math.ceil(total / 10);
          setWireCount(wireFrameSets);
          totPrice += wireFrameSets * 24.99;
        } else {
          const wireFramesProHex = 1.785;
          const wireFramesTotal = total * wireFramesProHex;
          const wireFrameSets = Math.ceil(wireFramesTotal / 5);
          setWireCount(wireFrameSets);
          totPrice += wireFrameSets * 24.99;
        }

      }
      
    } else if (((cols === 3 && rows === 2) || (cols === 1 && rows === 5)) && !withAluminiumFrame && !showFullFrame) {
      totPrice = 199;
      //@TODO duplicate code.
      if (withWireFrame) {
        if (withAluminiumFrame) {
          const wireFrameSets = Math.ceil(total / 10);
          setWireCount(wireFrameSets);
          totPrice += wireFrameSets * 24.99;
        } else {
          const wireFramesProHex = 1.785;
          const wireFramesTotal = total * wireFramesProHex;
          const wireFrameSets = Math.ceil(wireFramesTotal / 5);
          setWireCount(wireFrameSets);
          totPrice += wireFrameSets * 24.99;
        }

      }
    }

    setTotalPrice(round2(totPrice));
    setTotalNettoPrice(round2(totPrice / 1.19));

    // Originalpreis vor Rabatt berechnen (nur wenn Rabatt > 0)
    const discountPriceFactor = DISCOUNT_PERCENT > 0 ? (100 - DISCOUNT_PERCENT) / 100 : 1;

    setDiscountTotalPrice(round2(totPrice * discountPriceFactor));
    setDiscountNettoPrice(round2((totPrice / 1.19) * discountPriceFactor));

    // Maße nur zeichnen, wenn die 2D-Ansicht tatsächlich gemountet ist.
    if (canvas && ctx) {
      createCanvasSizeInfos(ctx, canvas, scale, outerSizeWidth, outerSizeHeight);
    }
  }, [width, length, withAluminiumFrame, withWireFrame, showFullFrame, typeMode, viewMode]);

  const calculateHexCount = (widthVal, lengthVal) => {
    const hexWidth = 2 * hexRadius;
    const hexHeight = Math.sqrt(3) * hexRadius;
    const horizSpacing = isHorizontal ? hexHeight : 0.75 * hexWidth;
    const vertSpacing = isHorizontal ? hexWidth : hexHeight;
    const cols = Math.floor(widthVal / horizSpacing);
    const rows = Math.floor(lengthVal / vertSpacing);
    return { cols, rows };
  };

  const calculateHexTotal = (cols, rows) => {
    let total = 0;
    for (let col = 0; col < cols; col++) {
      total += (col % 2 === 1) ? rows - 1 : rows;
    }
    return total;
  };

  const handleInputChange = (e, setter) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) setter(value);
  };

  function ToggleRow({ label, checked, onChange, disabled = false }) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 0",
          opacity: disabled ? 0.45 : 1
        }}
      >
        <span
          style={{
            color: disabled ? "#64748b" : "inherit"
          }}
        >
          {label}
        </span>

        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            onChange(!checked);
          }}
          style={{
            border: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            borderRadius: "999px",
            padding: "4px",

            background: disabled
              ? "#1f2937"
              : checked
                ? "linear-gradient(135deg,#10b981,#059669)"
                : "#374151",

            transition: "all .25s ease",
            minWidth: "90px",
            height: "28px",
            position: "relative"
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "4px",

              // Bei disabled immer optisch auf NEIN
              left: disabled
                ? "4px"
                : checked
                  ? "48px"
                  : "4px",

              width: "38px",
              height: "20px",
              borderRadius: "999px",
              background: disabled ? "#64748b" : "white",
              transition: "all .25s ease"
            }}
          />

          <span
            style={{
              position: "absolute",

              // Bei disabled immer NEIN anzeigen
              left: disabled
                ? "48px"
                : checked
                  ? "12px"
                  : "48px",

              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "12px",
              fontWeight: "600",

              color: disabled
                ? "#64748b"
                : checked
                  ? "white"
                  : "#d1d5db",

              transition: "all .25s ease"
            }}
          >
            {disabled ? "NEIN" : checked ? "JA" : "NEIN"}
          </span>
        </button>
      </div>
    );
  }

  function SegmentedControl({
    label,
    value,
    options,
    onChange
  }) {
    return (
      <div style={{ marginBottom: "6px" }}>
        <div
          style={{
            marginBottom: "6px",
            fontSize: "14px"
          }}
        >
          {label}
        </div>

        <div
          style={{
            display: "flex",
            backgroundColor: "#374151",
            borderRadius: "8px",
            overflow: "hidden"
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => onChange(option.value)}
              style={{
                flex: 1,
                padding: "7px",
                border: "none",
                cursor: "pointer",
                backgroundColor:
                  value === option.value
                    ? "#3b82f6"
                    : "transparent",
                color: "white",
                fontWeight:
                  value === option.value
                    ? "bold"
                    : "normal"
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const drawHexagons = (cols, rows, total, canvas, ctx, scale) => {
    console.log("d");
    //Dadurch wird alles schärfer
    canvas.width = (width + offsetX * 2) * scale;
    canvas.height = (length + offsetY * 2) * scale;
    canvas.style.width = `${width + offsetX * 2}px`;
    canvas.style.height = `${length + offsetY * 2}px`;
    ctx.setTransform(scale, 0, 0, scale, 0, 0); // Skalierung aktivieren

    const hexWidth = 2 * hexRadius;
    const hexHeight = Math.sqrt(3) * hexRadius;
    const horizSpacing = isHorizontal ? hexHeight : 0.75 * hexWidth;
    const vertSpacing = isHorizontal ? hexWidth : hexHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        if (col % 2 === 1 && row === rows - 1) continue;
        const x = col * horizSpacing + hexRadius + offsetX;
        const y = (col % 2 === 0)
          ? row * vertSpacing + hexHeight / 2 + offsetY
          : row * vertSpacing + hexHeight + offsetY;

        if (x + hexRadius > width + offsetX || y + hexHeight / 2 > length + offsetY) continue;
        minX = Math.min(minX, x - hexRadius);
        maxX = Math.max(maxX, x + hexRadius);
        minY = Math.min(minY, y - hexHeight / 2);
        maxY = Math.max(maxY, y + hexHeight / 2);
      }
    }

    if (withAluminiumFrame) {
      drawAluminiumFrame(ctx, minX, minY, maxX, maxY, showFullFrame, rows);
    }
    const edgeSet = new Set();
    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        if (col % 2 === 1 && row === rows - 1) continue;
        const x = col * horizSpacing + hexRadius + offsetX;
        const y = (col % 2 === 0)
          ? row * vertSpacing + hexHeight / 2 + offsetY
          : row * vertSpacing + hexHeight + offsetY;

        if (x + hexRadius > width + offsetX || y + hexHeight / 2 > length + offsetY) continue;
        drawHex(ctx, x, y, hexRadius, edgeSet);
      }
    }
    //TODO hier liegt ein Fehler vor, der noch untersucht werden muss. Es fehlten bei manchen Größen ein Stripe.
    let edgeSize = edgeSet.size + 1;
    setTotalHexagonStripes(edgeSize);

    const cornerMap = new Map();

    for (let key of edgeSet) {
      const [a, b] = JSON.parse(key);
      const keyA = `${a[0]},${a[1]}`;
      const keyB = `${b[0]},${b[1]}`;

      cornerMap.set(keyA, (cornerMap.get(keyA) || 0) + 1);
      cornerMap.set(keyB, (cornerMap.get(keyB) || 0) + 1);
    }

    let connectors2 = 0;
    let connectors3 = 0;

    let connectors2Empty = 0;
    let connectors3Empty = 0;

    for (let count of cornerMap.values()) {
      if (count === 2) connectors2++;
      else if (count === 3) connectors3++;
    }

    let cable = 1;
    /*if (total >= 23) {
      connectors3Empty = rows;
      const multiplicator = Math.floor(total / 23);
      connectors3Empty = multiplicator * connectors3Empty;
      connectors3 = connectors3 - connectors3Empty;
      cable = multiplicator + 1;
      //TODO rows und cols verwechselt
    }*/
    connectors3 = connectors3 + cable;
    connectors2 = connectors2 - cable;
    setConnectors({ two: connectors2, three: connectors3, twoempty: connectors2Empty, threeempty: connectors3Empty, cable: cable });

    if (showFullFrame) {
      drawFullFrame(ctx, minX, minY, maxX, maxY);
    }
  };
  
  const drawHex = (ctx, x, y, radius, edgeSet) => {
    ctx.beginPath();
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 180) * (60 * i);
      const x_i = x + radius * Math.cos(angle);
      const y_i = y + radius * Math.sin(angle);
      points.push([round(x_i), round(y_i)]);
      if (i === 0) ctx.moveTo(x_i, y_i);
      else ctx.lineTo(x_i, y_i);
    }
    for (let i = 0; i < 6; i++) {
      const a = points[i];
      const b = points[(i + 1) % 6];

      const key = JSON.stringify(a[0] < b[0] || (a[0] === b[0] && a[1] <= b[1]) ? [a, b] : [b, a]);
      edgeSet.add(key);
    }
    ctx.closePath();
    ctx.shadowColor = lightSettings[typeMode].shadowColor;
    ctx.shadowBlur = 15;
    ctx.strokeStyle = lightSettings[typeMode].shadowColor;
    ctx.lineWidth = 4;
    ctx.stroke();
  };

  const drawAluminiumFrame = (ctx, minX, minY, maxX, maxY, showFullFrame, rows) => {
    const stab = 120;
    
    const hexAreaWidth = maxX - minX;
    let hexAreaHeight = maxY - minY;

    hexAreaHeight = Math.ceil(hexAreaHeight);
    const division = rows / 3;
    // Nur wenn division eine ganze Zahl ist, addiere diesen Wert
    if (Number.isInteger(division)) {
      hexAreaHeight += division;
    }

    // Immer sauber aufrunden
    let frameWidth = Math.ceil(hexAreaWidth / stab) * stab;
    let frameHeight = Math.ceil(hexAreaHeight / stab) * stab;

    if (!showFullFrame) {
      if (frameWidth - hexAreaWidth > 50) frameWidth -= stab;
      if (frameHeight - hexAreaHeight > 30) frameHeight -= stab;
    }
    
    calcualteFrameParts(frameWidth, stab, frameHeight);

    // Exakt mittig ausrichten um die Hexagonfläche
    const frameX = minX - (frameWidth - hexAreaWidth) / 2;
    const frameY = minY - (frameHeight - hexAreaHeight) / 2;

    // Rahmen zeichnen
    ctx.beginPath();
    ctx.rect(frameX, frameY, frameWidth, frameHeight);
    ctx.shadowColor = "#9ca3af";
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 3;
    ctx.stroke();

    // Innenlinien (Gitter)
    for (let x = frameX + stab; x < frameX + frameWidth; x += stab) {
      ctx.beginPath();
      ctx.moveTo(x, frameY);
      ctx.lineTo(x, frameY + frameHeight);
      ctx.stroke();
    }

    for (let y = frameY + stab; y < frameY + frameHeight; y += stab) {
      ctx.beginPath();
      ctx.moveTo(frameX, y);
      ctx.lineTo(frameX + frameWidth, y);
      ctx.stroke();
    }
  };
 /*const horizontalStabs = Math.ceil(hexAreaWidth / stab);
    const verticalStabs = Math.ceil(hexAreaHeight / stab);
    const totalAluStripes = horizontalStabs * verticalStabs * 2;    
    const angle90 = 4;
    const angle180 = (horizontalStabs - 1) * verticalStabs + (verticalStabs - 1) * horizontalStabs;
    const crosses = (horizontalStabs - 1) * (verticalStabs - 1);

    setAluFrameParts({
      stripes: totalAluStripes,
      angle90,
      angle180,
      crosses
    });*/

  const drawFullFrame = (ctx, minX, minY, maxX, maxY) => {
    const hexAreaWidth = maxX - minX;
    let hexAreaHeight = Math.ceil(maxY - minY);
    let frameWidth = 120, frameHeight = 120;

    while (frameWidth < hexAreaWidth) frameWidth += 120;
    
    const { cols, rows } = calculateHexCount(width, length);
    hexAreaHeight = Math.ceil(hexAreaHeight);
    const division = rows / 3;
    // Nur wenn division eine ganze Zahl ist, addiere diesen Wert
    if (Number.isInteger(division)) {
      hexAreaHeight += division;
    }
    while (frameHeight <= hexAreaHeight) frameHeight += 120;

    const frameX = minX + (hexAreaWidth - frameWidth) / 2;
    const frameY = minY + (hexAreaHeight - frameHeight) / 2;

    ctx.beginPath();
    ctx.rect(frameX, frameY, frameWidth, frameHeight);
    ctx.shadowColor = lightSettings[typeMode].shadowColor;
    ctx.shadowBlur = 15;
    ctx.strokeStyle = lightSettings[typeMode].shadowColor;
    ctx.lineWidth = 4;
    ctx.stroke();
  };

  const buttonStyle = {
    marginBottom: "8px",
    padding: "8px 16px",
    background: "#374151",
    borderRadius: "8px",
    color: "white",
    border: "none",
    cursor: "pointer",
    width: "100%"
  };

  function drawWatermark(ctx, canvas, text = "WATERMARK") {
    const { width, height } = canvas;
    ctx.save();

    // Stil des Wasserzeichens
    //ctx.globalAlpha = 0.1; // Transparenz
    ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
    ctx.font = "32px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Drehung für diagonalen Effekt
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 4);

    // Wiederhole den Text über das Canvas
    const step = 200;
    for (let x = -width; x < width * 2; x += step) {
      for (let y = -height; y < height * 2; y += step) {
        ctx.fillText(text, x - width / 2, y - height / 2);
      }
    }

    ctx.restore();
  }


  // Liefert immer das Bild der aktuell sichtbaren Ansicht.
  // In 3D wird direkt das WebGL-Canvas verwendet; in 2D wird wie bisher
  // ein schwarzer Hintergrund hinter die Zeichnung gelegt.
  const getCurrentPreviewImage = () => {
    if (viewMode === "3d") {
      const canvas3d = threeDCanvasRef.current;
      if (!canvas3d) return null;
      return canvas3d.toDataURL("image/png");
    }

    const canvas = canvasRef.current;
    if (!canvas) return null;
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.fillStyle = "#000000";
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(canvas, 0, 0);
    return tempCanvas.toDataURL("image/png");
  };

  const handleDownload = () => {
    const imageBase64 = getCurrentPreviewImage();
    if (!imageBase64) {
      window.alert("Die aktuelle Ansicht ist noch nicht verfügbar.");
      return;
    }
    const link = document.createElement("a");
    link.download = viewMode === "3d" ? "hexagon-konfiguration-3d.png" : "hexagon-konfiguration.png";
    link.href = imageBase64;
    link.click();
  };

  // ── Zum Warenkorb hinzufügen ─────────────────────────────────────────────────
  // Der Warenkorb verwendet bewusst immer die 2D-Vorschau. Dadurch bleibt das
  // bestehende Produktbild unabhängig von der gerade verwendeten 3D-Kamera.
  const get2DPreviewImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    tempCtx.fillStyle = "#000000";
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    tempCtx.drawImage(canvas, 0, 0);
    return tempCanvas.toDataURL("image/png");
  };

  const addCurrentConfigurationToCart = (imageBase64) => {
    if (!imageBase64) {
      window.alert("Die 2D-Ansicht ist noch nicht verfügbar.");
      return;
    }

    let lichtfarbe = typeMode === "4000" ? warmweiss : kaltweiss;
    const frameText = [
      withAluminiumFrame ? "mit Alurahmen" : null,
      withWireFrame ? "mit Drahtseilen" : null,
      showFullFrame ? "mit LED Rahmen" : null,
    ].filter(Boolean).join(", ");

    const priceHexagon = DISCOUNT_PERCENT > 0 ? discountTotalPrice : totalPrice;
    const title = `${totalHexagons} Grid Hexagon${frameText ? " – " + frameText : ""} ${lichtfarbe}`;

    const newItem = {
      id: Date.now(), title, lichtfarbe, hexCount: { ...hexCount }, totalHexagons,
      withAluminiumFrame, withWireFrame, showFullFrame, typeMode,
      outerSize: { ...outerSize }, price: priceHexagon, nettoPrice: discountNettoPrice,
      totalHexagonStripes, totalFrameStripes, connectors: { ...connectors },
      frameConnectors: { ...frameConnectors }, aluFrameParts: { ...aluFrameParts },
      wireCount, imageBase64,
    };

    // Kartons für dieses einzelne Design berechnen
    newItem.cartons = calculateCartons(newItem);

    setCart(prev => [...prev, newItem]);
    setShowCart(true);
    setAddedToCartMsg(true);
    setTimeout(() => setAddedToCartMsg(false), 2500);
  };

  const handleAddToCart = () => {
    if (viewMode === "3d") {
      pendingAddToCartRef.current = true;
      setViewMode("2d");
      return;
    }
    addCurrentConfigurationToCart(get2DPreviewImage());
  };

  // Nach einem Warenkorb-Klick in 3D zuerst 2D mounten und zeichnen lassen.
  // Zwei Frames geben React und dem Canvas-Effect Zeit, die aktuelle Konfiguration
  // sichtbar zu rendern, bevor daraus das Warenkorb-Bild erzeugt wird.
  useEffect(() => {
    if (viewMode !== "2d" || !pendingAddToCartRef.current) return;

    let frame1 = 0;
    let frame2 = 0;
    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        pendingAddToCartRef.current = false;
        addCurrentConfigurationToCart(get2DPreviewImage());
      });
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, [viewMode]);

  const handleRemoveFromCart = (id) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  // ── Warenkorb-Gesamtbestellung an Shopify übertragen ────────────────────────
  // Karton-Text bleibt in der Bestellnotiz; echte Varianten sind Komponenten.
  const formatCartonsOnly = (item) => {
    const boxes = calculateCartonContents(item);
    const totalWeight = boxes.reduce((sum, box) => sum + box.weight, 0);
    const lines = [
      `Anzahl Kartons: ${boxes.length}`,
      '',
    ];
    boxes.forEach((box) => {
      lines.push(`KARTON ${box.name.replace(/^Karton\s*/i, '')}`);
      lines.push(`Maße: ${box.dimensions}`);
      lines.push(`Gewicht: ${box.weight.toFixed(2).replace('.', ',')} kg`);
      lines.push('Inhalt:');
      box.parts.forEach((part) => {
        // 1 Stromkabel mit Stopfen ist EIN Lagerartikel, keine getrennte Stopfenposition.
        if (/^(Hexagon Kabel|LED Kabel|Weißer Stopfen|LED weißer Stopfen)$/i.test(part.name)) {
          if (/Stopfen/i.test(part.name)) return;
          lines.push(`  - ${part.quantity} x Stromkabel mit Stopfen`);
        } else {
          lines.push(`  - ${part.quantity} x ${part.name}`);
        }
      });
      lines.push('');
    });
    lines.push(`Gesamtgewicht: ${totalWeight.toFixed(2).replace('.', ',')} kg`);
    if (boxes.length >= 5) lines.push('Hinweis: Palettenversand prüfen.');
    return lines.join('\n').trim();
  };

  const handleCartCheckout = async () => {
    setCartIsLoading(true);
    try {
      const items = cart.map(item => ({
        title: `Hexagon Konfigurator ${item.totalHexagons} Grid ${item.showFullFrame ? 'mit' : 'ohne'} LED Rahmen${item.withAluminiumFrame ? ', mit Aluminiumrahmen' : ''}${item.withWireFrame ? ', mit Drahtseilen' : ''} ${item.lichtfarbe}`,
        price: item.price,
        imageBase64: item.imageBase64,
        cartonInstructions: formatCartonsOnly(item),
        typeMode: item.typeMode,
        totalHexagonStripes: item.totalHexagonStripes,
        totalFrameStripes: item.totalFrameStripes,
        connectors: item.connectors,
        frameConnectors: item.frameConnectors,
        aluFrameParts: item.aluFrameParts,
        wireCount: item.wireCount,
        showFullFrame: item.showFullFrame,
        withAluminiumFrame: item.withAluminiumFrame,
        withWireFrame: item.withWireFrame,
      }));
      const response = await fetch("/api/create-checkout-bundle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }), // ← alle auf einmal
      });

      /*const text = await response.text();
      let result = {};
      try { result = JSON.parse(text); } catch (e) { throw new Error("Ungültige Serverantwort"); }

      if (result.success) {
        setCart([]);
        setCartIsLoading(false);
        window.top.location.href = result.checkoutUrl;
      } else {
        throw new Error("Shopify Fehler");
      }*/

      const text = await response.text();

      console.log("Checkout HTTP-Status:", response.status);
      console.log("Checkout Serverantwort:", text);

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        throw new Error(
          `Ungültige Serverantwort (HTTP ${response.status}): ${text.slice(0, 500)}`
        );
      }

      if (!response.ok || !result.success) {
        console.error("Checkout fehlgeschlagen:", result);

        throw new Error(
          result.error ||
          result.message ||
          `Checkout fehlgeschlagen (HTTP ${response.status})`
        );
      }

      if (!result.checkoutUrl) {
        throw new Error("Checkout-URL fehlt in der Serverantwort.");
      }

      setCart([]);
      setCartIsLoading(false);
      window.top.location.href = result.checkoutUrl;

    } catch (error) {
      alert("Fehler beim Erstellen des Checkout-Links.");
      console.error(error);
      setCartIsLoading(false);
    }
  };

  const sectionTitle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: "#9ca3af",
    textTransform: "uppercase",
    marginBottom: "6px",
    letterSpacing: "0.04em"
  };



  const panelStyle = {
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: "12px",
    padding: "10px",
    background: "linear-gradient(180deg, rgba(15,23,42,.88), rgba(15,23,42,.58))",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.04)"
  };

  const templateButtonStyle = (active) => ({
    minHeight: "48px",
    border: active ? "1px solid #d7b56d" : "1px solid rgba(148,163,184,.20)",
    borderRadius: "10px",
    color: "#f8fafc",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "13px",
    lineHeight: 1.15,
    background: active
      ? "linear-gradient(135deg, rgba(215,181,109,.20), rgba(15,23,42,.88))"
      : "linear-gradient(180deg, rgba(30,41,59,.72), rgba(15,23,42,.84))",
    boxShadow: active ? "0 0 0 1px rgba(215,181,109,.18), 0 10px 28px rgba(215,181,109,.08)" : "none"
  });

  const controlLineStyle = {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: "8px",
    alignItems: "center"
  };

  const mutedSmallStyle = { color: "#94a3b8", fontSize: "13px", marginTop: "2px" };

  const stepperStyle = {
    display: "grid",
    gridTemplateColumns: "38px 48px 38px",
    border: "1px solid rgba(148,163,184,.16)",
    borderRadius: "10px",
    overflow: "hidden",
    background: "rgba(2,6,23,.42)"
  };

  const stepperButtonStyle = {
    height: "34px",
    border: "none",
    background: "rgba(30,41,59,.86)",
    color: "white",
    cursor: "pointer",
    fontSize: "20px",
    lineHeight: 1
  };

  const stepperValueStyle = {
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    background: "rgba(2,6,23,.42)"
  };

  const dividerStyle = { height: "1px", background: "rgba(148,163,184,.14)" };

  const metricPillStyle = {
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: "10px",
    padding: "8px",
    textAlign: "center",
    color: "#e2e8f0",
    background: "rgba(2,6,23,.28)",
    fontWeight: 700
  };

  const priceBoxStyle = {
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: "14px",
    padding: "12px",
    textAlign: "center",
    background: "radial-gradient(circle at 50% 0%, rgba(34,197,94,.12), transparent 46%), rgba(2,6,23,.54)"
  };

  const primaryButtonStyle = {
    minHeight: "44px",
    border: "none",
    borderRadius: "10px",
    color: "white",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 800,
    background: "linear-gradient(135deg,#35c776,#16a34a)",
    boxShadow: "0 14px 32px rgba(22,163,74,.28)"
  };

  const secondaryButtonStyle = {
    minHeight: "42px",
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: "10px",
    color: "white",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 700,
    background: "linear-gradient(180deg, rgba(41,37,36,.9), rgba(28,25,23,.9))"
  };

  const cartButtonStyle = {
    minHeight: "42px",
    border: "1px solid rgba(96,165,250,.36)",
    borderRadius: "10px",
    color: "white",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: 800,
    background: "linear-gradient(135deg,#2563eb,#1d4ed8)"
  };

  const tabStyle = {
    padding: "8px 14px",
    color: "#cbd5e1",
    fontWeight: 700,
    borderRadius: "9px",
    fontSize: "14px"
  };

  const tabActiveStyle = {
    ...tabStyle,
    color: "#d7b56d",
    background: "rgba(255,255,255,.07)"
  };

  function ThreeDPreview() {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraState = threeDCameraStateRef;
    const dragRef = useRef(null);

    useEffect(() => {
      const mount = mountRef.current;
      if (!mount) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color('#555b61');
      sceneRef.current = scene;

      const camera3d = new THREE.PerspectiveCamera(48, 1, 0.05, 100);
      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.18;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.domElement.style.width = '100%';
      renderer.domElement.style.height = '100%';
      renderer.domElement.style.display = 'block';
      renderer.domElement.style.cursor = 'grab';
      mount.appendChild(renderer.domElement);
      threeDCanvasRef.current = renderer.domElement;

      // Maße der 2D-Konfiguration werden direkt wiederverwendet. 100 cm = 1 m.
      const hexW = 2 * hexRadius;
      const hexH = Math.sqrt(3) * hexRadius;
      const spacingX = isHorizontal ? hexH : 0.75 * hexW;
      const spacingY = isHorizontal ? hexW : hexH;
      const centers = [];
      for (let col = 0; col < hexCount.cols; col++) {
        for (let row = 0; row < hexCount.rows; row++) {
          if (col % 2 === 1 && row === hexCount.rows - 1) continue;
          const x = col * spacingX + hexRadius;
          const y = col % 2 === 0 ? row * spacingY + hexH / 2 : row * spacingY + hexH;
          if (x + hexRadius <= width + 1 && y + hexH / 2 <= length + 1) centers.push({ x, y });
        }
      }

      if (!centers.length) return undefined;

      const minX = Math.min(...centers.map(p => p.x - hexRadius));
      const maxX = Math.max(...centers.map(p => p.x + hexRadius));
      const minY = Math.min(...centers.map(p => p.y - hexH / 2));
      const maxY = Math.max(...centers.map(p => p.y + hexH / 2));
      const center2DX = (minX + maxX) / 2;
      const center2DY = (minY + maxY) / 2;
      const cmToM = 0.01;
      // Höherer Raum; die Leuchte bleibt direkt unter der Decke montiert.
      const ceilingY = 5.20;
      const fixtureWidth = (maxX - minX) * cmToM;
      const fixtureDepth = (maxY - minY) * cmToM;
      const roomWidth = Math.max(6.5, fixtureWidth + 3.4);
      const roomDepth = Math.max(7.5, fixtureDepth + 3.8);

      // Geschlossener Raum wie in der Floor3D-Referenz: Boden + Wände + sichtbare Decke.
      const roomMaterial = new THREE.MeshStandardMaterial({ color: '#d9d9d5', roughness: 0.94, side: THREE.DoubleSide });
      const floorMaterial = new THREE.MeshStandardMaterial({ color: '#777b7e', roughness: 0.88 });
      const floor = new THREE.Mesh(new THREE.PlaneGeometry(roomWidth, roomDepth), floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.receiveShadow = true;
      scene.add(floor);
      const ceiling = new THREE.Mesh(new THREE.PlaneGeometry(roomWidth, roomDepth), roomMaterial);
      ceiling.rotation.x = Math.PI / 2;
      ceiling.position.y = ceilingY + 0.035;
      ceiling.receiveShadow = true;
      scene.add(ceiling);
      const wallBack = new THREE.Mesh(new THREE.PlaneGeometry(roomWidth, ceilingY), roomMaterial);
      wallBack.position.set(0, ceilingY / 2, -roomDepth / 2);
      wallBack.receiveShadow = true;
      scene.add(wallBack);
      const wallLeft = new THREE.Mesh(new THREE.PlaneGeometry(roomDepth, ceilingY), roomMaterial);
      wallLeft.rotation.y = Math.PI / 2;
      wallLeft.position.set(-roomWidth / 2, ceilingY / 2, 0);
      wallLeft.receiveShadow = true;
      scene.add(wallLeft);

      scene.add(new THREE.HemisphereLight('#ffffff', '#34383c', 1.05));
      const roomFill = new THREE.DirectionalLight('#ffffff', 1.15);
      roomFill.position.set(4, 5, 4);
      scene.add(roomFill);

      const lightHex = typeMode === '4000' ? '#ffdca3' : '#e3f2ff';
      const ledMaterial = new THREE.MeshStandardMaterial({
        color: lightHex,
        emissive: lightHex,
        emissiveIntensity: 5.5,
        roughness: 0.2,
        metalness: 0.02,
        toneMapped: false
      });

      const toWorld = (x, y) => [
        (x - center2DX) * cmToM,
        ceilingY - 0.045,
        (y - center2DY) * cmToM
      ];

      const edgeMap = new Map();
      const pointKey = p => `${p[0].toFixed(4)},${p[1].toFixed(4)}`;
      centers.forEach(c => {
        const pts = Array.from({ length: 6 }, (_, i) => {
          const a = Math.PI / 3 * i;
          return [c.x + hexRadius * Math.cos(a), c.y + hexRadius * Math.sin(a)];
        });
        pts.forEach((a, i) => {
          const b = pts[(i + 1) % 6];
          const ka = pointKey(a), kb = pointKey(b);
          edgeMap.set(ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`, [a, b]);
        });
      });

      const tubeRadius = 0.018;
      const tubeGeometry = new THREE.CylinderGeometry(tubeRadius, tubeRadius, 1, 10);
      const up = new THREE.Vector3(0, 1, 0);
      const addTube = (a2, b2, material = ledMaterial, radiusScale = 1) => {
        const aw = toWorld(a2[0], a2[1]);
        const bw = toWorld(b2[0], b2[1]);
        const a = new THREE.Vector3(...aw), b = new THREE.Vector3(...bw);
        const dir = new THREE.Vector3().subVectors(b, a);
        const len = dir.length();
        const mesh = new THREE.Mesh(tubeGeometry, material);
        mesh.position.copy(a).add(b).multiplyScalar(0.5);
        mesh.quaternion.setFromUnitVectors(up, dir.clone().normalize());
        mesh.scale.set(radiusScale, len, radiusScale);
        mesh.castShadow = false;
        scene.add(mesh);
      };
      edgeMap.forEach(([a, b]) => addTube(a, b));

      // LED-Rahmen: dieselbe Außenabmessung wie in der 2D-Konfiguration.
      if (showFullFrame) {
        const pad = 18;
        const frame = [[minX-pad,minY-pad],[maxX+pad,minY-pad],[maxX+pad,maxY+pad],[minX-pad,maxY+pad]];
        frame.forEach((a, i) => addTube(a, frame[(i + 1) % 4]));
      }

      // Aluminiumrahmen und Drahtseile sind Montage-/Zubehöroptionen und werden in 3D bewusst nicht dargestellt.

      // Lichtwirkung nach unten. Anzahl und Verteilung richten sich nach der konfigurierten Fläche.
      const area = Math.max(1, fixtureWidth * fixtureDepth);
      const areaLight = new THREE.RectAreaLight(lightHex, Math.min(18, 7 + area * 0.65), Math.max(1.2, fixtureWidth), Math.max(1.2, fixtureDepth));
      areaLight.position.set(0, ceilingY - 0.08, 0);
      areaLight.rotation.x = -Math.PI / 2;
      scene.add(areaLight);

      const spotCount = Math.max(2, Math.min(5, Math.ceil(fixtureDepth / 1.7)));
      for (let i = 0; i < spotCount; i++) {
        const z = spotCount === 1 ? 0 : -fixtureDepth * 0.38 + (fixtureDepth * 0.76 * i / (spotCount - 1));
        const spot = new THREE.SpotLight(lightHex, 42 / spotCount, ceilingY + 4, 0.78, 0.82, 1.35);
        spot.position.set(0, ceilingY - 0.09, z);
        spot.target.position.set(0, 0, z);
        scene.add(spot, spot.target);
      }
      const shadowSpot = new THREE.SpotLight(lightHex, 55, ceilingY + 5, 1.05, 0.75, 1.2);
      shadowSpot.position.set(0, ceilingY - 0.1, 0);
      shadowSpot.target.position.set(0, 0, 0);
      shadowSpot.castShadow = true;
      shadowSpot.shadow.mapSize.set(512, 512);
      shadowSpot.shadow.bias = -0.00012;
      scene.add(shadowSpot, shadowSpot.target);

      // Sehr weicher Lichtfleck am Boden, damit die Wirkung auch ohne teure GI sichtbar bleibt.
      const glowTexCanvas = document.createElement('canvas');
      glowTexCanvas.width = glowTexCanvas.height = 256;
      const gctx = glowTexCanvas.getContext('2d');
      const grad = gctx.createRadialGradient(128,128,8,128,128,128);
      const rgb = typeMode === '4000' ? '255,220,163' : '227,242,255';
      grad.addColorStop(0, `rgba(${rgb},0.34)`); grad.addColorStop(0.55, `rgba(${rgb},0.13)`); grad.addColorStop(1, `rgba(${rgb},0)`);
      gctx.fillStyle = grad; gctx.fillRect(0,0,256,256);
      const glow = new THREE.Mesh(
        new THREE.PlaneGeometry(Math.max(2.2, fixtureWidth * 1.7), Math.max(2.5, fixtureDepth * 1.55)),
        new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(glowTexCanvas), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending })
      );
      glow.rotation.x = -Math.PI / 2;
      glow.position.y = 0.008;
      scene.add(glow);

      const updateCamera = () => {
        const s = cameraState.current;
        const cp = Math.cos(s.pitch);
        camera3d.position.set(Math.sin(s.yaw) * cp * s.distance, s.targetY + Math.sin(s.pitch) * s.distance, Math.cos(s.yaw) * cp * s.distance);
        camera3d.lookAt(0, s.targetY, 0);
      };
      updateCamera();

      let raf = 0;
      const render = () => { renderer.render(scene, camera3d); raf = requestAnimationFrame(render); };
      render();
      const resize = () => {
        const r = mount.getBoundingClientRect();
        if (!r.width || !r.height) return;
        renderer.setSize(r.width, r.height, false);
        camera3d.aspect = r.width / r.height;
        camera3d.updateProjectionMatrix();
      };
      const ro = new ResizeObserver(resize); ro.observe(mount); resize();

      const down = e => { dragRef.current = { x:e.clientX, y:e.clientY }; renderer.domElement.setPointerCapture?.(e.pointerId); renderer.domElement.style.cursor='grabbing'; };
      const move = e => {
        if (!dragRef.current) return;
        const dx=e.clientX-dragRef.current.x, dy=e.clientY-dragRef.current.y;
        dragRef.current={x:e.clientX,y:e.clientY};
        cameraState.current.yaw -= dx * 0.006;
        cameraState.current.pitch = Math.max(0.12, Math.min(1.18, cameraState.current.pitch + dy * 0.004));
        updateCamera();
      };
      const upHandler = () => { dragRef.current=null; renderer.domElement.style.cursor='grab'; };
      const wheel = e => { e.preventDefault(); cameraState.current.distance = Math.max(4.2, Math.min(18, cameraState.current.distance + e.deltaY * 0.008)); updateCamera(); };
      renderer.domElement.addEventListener('pointerdown', down);
      renderer.domElement.addEventListener('pointermove', move);
      renderer.domElement.addEventListener('pointerup', upHandler);
      renderer.domElement.addEventListener('pointercancel', upHandler);
      renderer.domElement.addEventListener('wheel', wheel, { passive:false });

      return () => {
        cancelAnimationFrame(raf); ro.disconnect();
        renderer.domElement.removeEventListener('pointerdown', down);
        renderer.domElement.removeEventListener('pointermove', move);
        renderer.domElement.removeEventListener('pointerup', upHandler);
        renderer.domElement.removeEventListener('pointercancel', upHandler);
        renderer.domElement.removeEventListener('wheel', wheel);
        scene.traverse(obj => { if (obj.geometry) obj.geometry.dispose?.(); if (obj.material) { const mats=Array.isArray(obj.material)?obj.material:[obj.material]; mats.forEach(m=>{ if(m.map) m.map.dispose?.(); m.dispose?.(); }); } });
        if (threeDCanvasRef.current === renderer.domElement) threeDCanvasRef.current = null;
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    }, [width, length, hexCount, showFullFrame, typeMode, isHorizontal]);

    return <div style={{position:'absolute', inset:0, paddingTop:'52px', background:'#555b61'}}>
      <div ref={mountRef} style={{width:'100%',height:'100%'}} />
      <div style={{position:'absolute',left:'18px',top:'70px',padding:'8px 11px',borderRadius:'10px',background:'rgba(2,6,23,.72)',border:'1px solid rgba(148,163,184,.2)',color:'#e2e8f0',fontSize:'12px',pointerEvents:'none'}}>3D-Raum</div>
      <div style={{position:'absolute',right:'18px',bottom:'18px',padding:'8px 11px',borderRadius:'10px',background:'rgba(2,6,23,.72)',border:'1px solid rgba(148,163,184,.2)',color:'#cbd5e1',fontSize:'12px',pointerEvents:'none'}}>Ziehen: drehen · Mausrad: Zoom</div>
    </div>;
  }

  return (
    <div
      style={{
        padding: isMobile ? "8px" : "10px",
        background:
          "radial-gradient(circle at 70% 15%, rgba(59,130,246,.13), transparent 34%), linear-gradient(135deg,#070b12 0%,#0b111c 48%,#06080d 100%)",
        color: "#f8fafc",
        minHeight: "100vh",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}
      translate="no"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "370px minmax(0, 1fr)",
          gap: "9px",
          maxWidth: "1440px",
          margin: "0 auto"
        }}
      >
        {/* Sidebar */}
        <aside
          style={{
            border: "1px solid rgba(148,163,184,.22)",
            borderRadius: "16px",
            padding: "12px",
            background:
              "linear-gradient(180deg, rgba(15,23,42,.96), rgba(2,6,23,.96))",
            boxShadow: "0 22px 70px rgba(0,0,0,.45)",
            display: "flex",
            flexDirection: "column",
            gap: "9px"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "2px 0 0"
            }}
          >
            {/* Button zurück zur Homepage */}
            <button
              type="button"
              onClick={() => {
                window.top.location.href = "https://floor-brothers.de/";
              }}
              style={{
                width: "38px",
                height: "38px",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
                border: "1px solid rgba(148,163,184,.22)",
                borderRadius: "10px",
                background: "rgba(15,23,42,.85)",
                color: "#d7b56d",
                fontSize: "22px",
                fontWeight: "bold",
                cursor: "pointer"
              }}
              title="Zur Homepage"
            >
              ←
            </button>

            {/* Floor Brothers Logo */}
            <a
              href="https://floor-brothers.de/"
              onClick={(e) => {
                e.preventDefault();
                window.top.location.href = "https://floor-brothers.de/";
              }}
            >
              <img
                src="https://konfigurator.floor-brothers.de/images/logo/floor-brothers.png"
                alt="Floor Brothers Logo"
                style={{
                  maxWidth: "175px",
                  height: "auto",
                  display: "block"
                }}
              />
            </a>
          </div>

          <h1
            style={{
              margin: "0 0 4px",
              fontSize: "21px",
              lineHeight: 1.15,
              textAlign: "center",
              letterSpacing: "-.02em"
            }}
          >
            Hexagon Konfigurator
          </h1>

          <section style={panelStyle}>
            <div style={sectionTitle}>▦ Vorlagen</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <button style={templateButtonStyle(width === 230 && length === 160)} onClick={() => { setWidth(230); setLength(160); setShowFullFrame(false); }}>5er Hexagon
              </button>
              <button style={templateButtonStyle(width === 230 && length === 320)} onClick={() => { setWidth(230); setLength(320); setShowFullFrame(false); }}>11er Hexagon
              </button>
              <button style={templateButtonStyle(width === 230 && length === 400 && !showFullFrame)} onClick={() => { setWidth(230); setLength(400); setShowFullFrame(false); }}>Standard<br />14 Grid
              </button>
              <button
                disabled={
                  (typeMode === "4000" && !ENABLE_LED_FRAME_FOR_4000K) ||
                  (typeMode === "6500" && !ENABLE_LED_FRAME_FOR_6500K)
                }
                style={{
                  ...templateButtonStyle(
                    width === 230 &&
                    length === 400 &&
                    showFullFrame
                  ),
                  opacity:
                    (typeMode === "4000" && !ENABLE_LED_FRAME_FOR_4000K) ||
                    (typeMode === "6500" && !ENABLE_LED_FRAME_FOR_6500K)
                      ? 0.4
                      : 1,
                  cursor:
                    (typeMode === "4000" && !ENABLE_LED_FRAME_FOR_4000K) ||
                    (typeMode === "6500" && !ENABLE_LED_FRAME_FOR_6500K)
                      ? "not-allowed"
                      : "pointer"
                }}
                onClick={() => {
                  const ledFrameAllowed =
                    (typeMode === "4000" && ENABLE_LED_FRAME_FOR_4000K) ||
                    (typeMode === "6500" && ENABLE_LED_FRAME_FOR_6500K);

                  if (!ledFrameAllowed) return;

                  setWidth(230);
                  setLength(400);
                  setShowFullFrame(true);
                }}
              >
                14 Grid
                <br />
                mit LED Rahmen
              </button>
            </div>
          </section>

          <section style={panelStyle}>
            <div style={sectionTitle}>⌁ Größe</div>
            <div style={{ display: "grid", gap: "9px" }}>
              <div style={controlLineStyle}>
                <div>
                  <strong>Reihen</strong>
                  <div style={mutedSmallStyle}>(Höhe)</div>
                </div>
                <div style={stepperStyle}>
                  <button style={stepperButtonStyle} onClick={() => {
                    if ((hexCount.rows == 2 && hexCount.cols == 1) || hexCount.rows > 2) {
                      const value = hexCount.rows - 1;
                      let hexHeight = Math.sqrt(3) * hexRadius;
                      const hexWidth = 2 * hexRadius;
                      const spacingY = isHorizontal ? hexWidth : hexHeight;
                      const newLength = Math.ceil((value - 1) * spacingY + hexHeight);
                      setLength(newLength);
                    }
                  }}>−</button>
                  <span style={stepperValueStyle}>{hexCount.rows}</span>
                  <button ref={buttonRef} style={stepperButtonStyle} onClick={() => {
                    if (hexCount.rows < 40) {
                      const value = hexCount.rows + 1;
                      let hexHeight = Math.sqrt(3) * hexRadius;
                      const hexWidth = 2 * hexRadius;
                      const spacingY = isHorizontal ? hexWidth : hexHeight;
                      const newLength = Math.ceil((value - 1) * spacingY + hexHeight);
                      setLength(newLength);
                    }
                  }}>+</button>
                </div>
                <span style={{ color: "#cbd5e1", minWidth: "62px", textAlign: "right" }}>{outerSize.height} cm</span>
              </div>

              <div style={dividerStyle} />

              <div style={controlLineStyle}>
                <div>
                  <strong>Spalten</strong>
                  <div style={mutedSmallStyle}>(Breite)</div>
                </div>
                <div style={stepperStyle}>
                  <button style={stepperButtonStyle} onClick={() => {
                    if (hexCount.cols > 1) {
                      const value = hexCount.cols - 1;
                      const hexWidth = 2 * hexRadius;
                      const hexHeight = Math.sqrt(3) * hexRadius;
                      const spacingX = isHorizontal ? hexHeight : 0.75 * hexWidth;
                      const newWidth = Math.ceil((value - 1) * spacingX + hexWidth);
                      setWidth(Math.ceil(newWidth));
                    }
                  }}>−</button>
                  <span style={stepperValueStyle}>{hexCount.cols}</span>
                  <button style={stepperButtonStyle} onClick={() => {
                    if (hexCount.cols < 40) {
                      const value = hexCount.cols + 1;
                      if (value == 2 && hexCount.rows == 1) buttonRef.current.click();
                      const hexWidth = 2 * hexRadius;
                      const hexHeight = Math.sqrt(3) * hexRadius;
                      const spacingX = isHorizontal ? hexHeight : 0.75 * hexWidth;
                      const newWidth = Math.ceil((value - 1) * spacingX + hexWidth);
                      setWidth(Math.ceil(newWidth));
                    }
                  }}>+</button>
                </div>
                <span style={{ color: "#cbd5e1", minWidth: "62px", textAlign: "right" }}>{outerSize.width} cm</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "4px" }}>
                <div style={metricPillStyle}>↔ {outerSize.width} × {outerSize.height} cm</div>
                <div style={metricPillStyle}>⬡ {totalHexagons} Hexagons</div>
              </div>
            </div>
          </section>

          <section style={panelStyle}>
            <div style={sectionTitle}>⚙ Optionen</div>
            <SegmentedControl
              label={
                typeMode === "4000"
                  ? "Farbtemperatur 4000K Neutralweiß"
                  : "Farbtemperatur 6500K Kaltweiß"
              }
              value={typeMode}
              options={[
                { value: "4000", label: "4000K" },
                { value: "6500", label: "6500K" }
              ]}
              onChange={(value) => {
                setTypeMode(value);

                const ledFrameAllowed =
                  (value === "4000" && ENABLE_LED_FRAME_FOR_4000K) ||
                  (value === "6500" && ENABLE_LED_FRAME_FOR_6500K);

                if (!ledFrameAllowed) {
                  setShowFullFrame(false);
                }
              }}
            />
            <ToggleRow
              label="LED Rahmen"
              checked={showFullFrame}
              onChange={setShowFullFrame}
              disabled={
                (typeMode === "4000" && !ENABLE_LED_FRAME_FOR_4000K) ||
                (typeMode === "6500" && !ENABLE_LED_FRAME_FOR_6500K)
              }
            />
            <ToggleRow label="Alurahmen" checked={withAluminiumFrame} onChange={setWithAluminiumFrame} />
            <ToggleRow label="Drahtseile" checked={withWireFrame} onChange={setWithWireFrame} />
          </section>

          <div style={priceBoxStyle}>
            <div style={{ color: "#a8b3c7", fontSize: "14px", marginBottom: "6px" }}>
              Gesamtpreis inkl. MwSt. & Versand
            </div>
            <div style={{ fontSize: "27px", lineHeight: 1, fontWeight: 800, color: "#35c776", letterSpacing: "-.03em" }}>
              {DISCOUNT_PERCENT > 0 && (
                <span style={{ textDecoration: "line-through", color: "#64748b", marginRight: "10px", fontSize: "18px" }}>
                  {formatPrice(totalPrice)} €
                </span>
              )}
              {formatPrice(discountTotalPrice)} €
            </div>
            <div style={{ marginTop: "6px", color: "#cbd5e1", fontSize: "13px" }}>
              Netto: {formatPrice(discountNettoPrice)} €
            </div>
          </div>

          {DISCOUNT_PERCENT > 0 && (
            <p style={{ margin: 0, textAlign: "center", fontWeight: 700 }}>
              Jetzt -{DISCOUNT_PERCENT}% Rabatt sichern.
            </p>
          )}

          <button style={primaryButtonStyle} onClick={handleAddToCart}>
            🛒 {addedToCartMsg ? "Hinzugefügt" : "In den Warenkorb"}
          </button>

          {cart.length > 0 && (
            <button style={cartButtonStyle} onClick={() => setShowCart(true)}>
              🛒 Warenkorb ({cart.length}) – {formatPrice(cart.reduce((s, i) => s + i.price, 0))} €
            </button>
          )}

          <span style={{ fontSize: "12px", color: "#94a3b8", textAlign: "center", marginTop: "0" }}>
            Abbildung dient der Veranschaulichung.
          </span>
        </aside>

        {/* Preview */}
        <main
          style={{
            border: "1px solid rgba(148,163,184,.22)",
            borderRadius: "16px",
            background:
              "linear-gradient(180deg, rgba(15,23,42,.9), rgba(2,6,23,.94))",
            boxShadow: "0 22px 70px rgba(0,0,0,.45)",
            overflow: "hidden",
            minHeight: isMobile ? "430px" : "calc(100vh - 20px)",
            position: "relative"
          }}
        >
          {/* Header oberhalb der Hexagon-Darstellung, nicht im Canvas */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              height: "52px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              padding: "0 16px",
              background: "rgba(2,6,23,.75)",
              borderBottom: "1px solid rgba(148,163,184,.18)",
              backdropFilter: "blur(10px)"
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center", width: "100%" }}>
              <button
                style={{ ...secondaryButtonStyle, width: "auto", minHeight: "36px", padding: "0 14px" }}
                onClick={handleDownload}
              >
                ⇩ Bild herunterladen
              </button>
              <div style={{ marginLeft: "auto", display: "flex", gap: "4px", padding: "3px", borderRadius: "10px", background: "rgba(15,23,42,.92)", border: "1px solid rgba(148,163,184,.22)" }}>
                <button onClick={() => setViewMode("2d")} style={{ border: 0, borderRadius: "7px", padding: "7px 12px", cursor: "pointer", fontWeight: 800, color: viewMode === "2d" ? "#071018" : "#cbd5e1", background: viewMode === "2d" ? "#d7b56d" : "transparent" }}>2D</button>
                <button onClick={() => setViewMode("3d")} style={{ border: 0, borderRadius: "7px", padding: "7px 12px", cursor: "pointer", fontWeight: 800, color: viewMode === "3d" ? "#071018" : "#cbd5e1", background: viewMode === "3d" ? "#d7b56d" : "transparent" }}>◈ 3D anzeigen</button>
              </div>
            </div>
          </div>

          {viewMode === "3d" ? <ThreeDPreview /> : <div
            ref={canvasWrapperRef}
            style={{
              width: "100%",
              height: "100%",
              minHeight: isMobile ? "430px" : "calc(100vh - 20px)",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              overflow: "hidden",
              padding: isMobile ? "70px 12px 18px" : "70px 30px 26px",
              background:
                "radial-gradient(circle at 50% 10%, rgba(226,232,240,.18), transparent 28%), linear-gradient(180deg,#111827 0%,#0b111a 48%,#171b23 49%,#29303a 100%)"
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                opacity: .36,
                background:
                  "linear-gradient(90deg, transparent 0 48%, rgba(255,255,255,.08) 49% 51%, transparent 52% 100%), linear-gradient(0deg, transparent 0 48%, rgba(255,255,255,.05) 49% 51%, transparent 52% 100%)",
                backgroundSize: "120px 120px"
              }}
            />
            <div
              style={{
                position: "relative",
                display: "inline-block",
                transformOrigin: "center center",
                transform: `scale(${scaleFactor})`,
                width: `${width + offsetX * 2}px`,
                height: `${length + offsetY * 2}px`,
                marginTop: isMobile ? "0" : "-18px",
                filter: "drop-shadow(0 0 26px rgba(226,232,240,.38))"
              }}
            >
              <canvas
                ref={canvasRef}
                width={width + offsetX * 2}
                height={length + offsetY * 2}
                style={{
                  backgroundColor: "rgba(2,6,23,.18)",
                  borderRadius: "14px",
                  border: "1px solid rgba(226,232,240,.18)",
                  display: "block",
                  boxShadow: "0 0 55px rgba(226,232,240,.20), inset 0 0 34px rgba(255,255,255,.06)"
                }}
              />
            </div>
          </div>}
        </main>
      </div>

      {showCart && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, backgroundColor: "rgba(0,0,0,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div style={{ background: "linear-gradient(180deg,#111827,#060a12)", border: "1px solid rgba(148,163,184,.24)", borderRadius: "18px", width: "100%", maxWidth: "760px", maxHeight: "90vh", overflowY: "auto", padding: "24px", color: "white", boxShadow: "0 30px 90px rgba(0,0,0,.6)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontSize: "22px" }}>Warenkorb</h2>
              <button onClick={() => setShowCart(false)} style={{ background: "rgba(15,23,42,.9)", border: "1px solid rgba(148,163,184,.22)", color: "#cbd5e1", borderRadius: "10px", fontSize: "20px", cursor: "pointer", width: "38px", height: "38px" }}>×</button>
            </div>

            {cart.length === 0 ? (
              <p style={{ color: "#9ca3af", textAlign: "center", padding: "40px 0" }}>Ihr Warenkorb ist leer.</p>
            ) : (
              <>
                {cart.map((item, idx) => (
                  <div key={item.id} style={{ backgroundColor: "rgba(15,23,42,.9)", borderRadius: "14px", border: "1px solid rgba(148,163,184,.18)", padding: "16px", marginBottom: "12px", display: "flex", gap: "16px", alignItems: "flex-start" }}>
                    <img src={item.imageBase64} alt="Vorschau" style={{ width: "110px", height: "86px", objectFit: "cover", borderRadius: "10px", border: "1px solid rgba(148,163,184,.2)", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "bold", marginBottom: "6px", fontSize: "15px" }}>Artikel {idx + 1}: {item.title}</div>
                      <div style={{ fontSize: "13px", color: "#cbd5e1", lineHeight: "1.6" }}>
                        <span>{item.hexCount.cols} Spalten × {item.hexCount.rows} Reihen ({item.totalHexagons} Hexagons)</span><br />
                        <span>{item.outerSize.width} cm × {item.outerSize.height} cm</span><br />
                        <span>{item.lichtfarbe}</span>{" · "}
                        <span>{item.withAluminiumFrame ? "Alurahmen" : "Kein Alurahmen"}</span>{" · "}
                        <span>{item.withWireFrame ? "Drahtseile" : "Keine Drahtseile"}</span>{" · "}
                        <span>{item.showFullFrame ? "LED Rahmen" : "Kein LED Rahmen"}</span>
                      </div>
                      <div style={{ marginTop: "8px", fontWeight: "bold", fontSize: "14px", color: "#35c776" }}>{formatPrice(item.price)} € <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: "normal" }}>(Netto: {formatPrice(item.nettoPrice)} €)</span></div>
                    </div>
                    <button onClick={() => handleRemoveFromCart(item.id)} style={{ background: "transparent", border: "1px solid #ef4444", color: "#ef4444", borderRadius: "8px", padding: "7px 10px", cursor: "pointer", fontSize: "13px", flexShrink: 0 }}>Entfernen</button>
                  </div>
                ))}

                <div style={{ borderTop: "1px solid rgba(148,163,184,.22)", marginTop: "16px", paddingTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: "bold" }}>Gesamtpreis: {formatPrice(cart.reduce((s, i) => s + i.price, 0))} €</div>
                    <div style={{ fontSize: "13px", color: "#94a3b8" }}>Netto: {formatPrice(cart.reduce((s, i) => s + i.nettoPrice, 0))} € · inkl. 19 % MwSt. und Versand</div>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button onClick={() => setShowCart(false)} style={{ padding: "10px 16px", backgroundColor: "transparent", border: "1px solid rgba(148,163,184,.28)", borderRadius: "8px", color: "#cbd5e1", cursor: "pointer", fontSize: "14px" }}>Weiter shoppen</button>
                    <button onClick={() => { if (window.confirm("Warenkorb wirklich leeren?")) setCart([]); }} style={{ padding: "10px 16px", backgroundColor: "transparent", border: "1px solid #ef4444", borderRadius: "8px", color: "#ef4444", cursor: "pointer", fontSize: "14px" }}>Leeren</button>
                    <button onClick={handleCartCheckout} disabled={cartIsLoading} style={{ padding: "10px 20px", background: cartIsLoading ? "#334155" : "linear-gradient(135deg,#2563eb,#1d4ed8)", border: "none", borderRadius: "8px", color: "white", cursor: cartIsLoading ? "not-allowed" : "pointer", fontSize: "15px", fontWeight: "bold", opacity: cartIsLoading ? 0.7 : 1 }}>{cartIsLoading ? "Wird übertragen..." : "Jetzt bestellen →"}</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

function createCanvasSizeInfos(ctx, canvas, scale, outerSizeWidth, outerSizeHeight) {
  ctx.font = "16px Arial";
  ctx.fillStyle = lightSettings[typeMode].shadowColor;
  ctx.textAlign = "center";

  // Oben die Breite (horizontal)
  const widthX = canvas.width / 2 / scale;
  const widthY = 20;
  ctx.fillText(`Breite: ${outerSizeWidth} cm`, widthX, widthY);
  ctx.fillText("Floor Brothers", widthX, widthY + 18); // "X" darunter

  // Links die Länge (vertikal, rotiert)
  ctx.save();
  const lengthX = 20;
  const lengthY = canvas.height / 2 / scale;
  ctx.translate(lengthX, lengthY);
  ctx.rotate(-Math.PI / 2);
  ctx.textAlign = "center";
  ctx.fillText(`Länge: ${outerSizeHeight} cm`, 0, 0);
  ctx.fillText("Floor Brothers", 0, 18); // "X" darunter
  ctx.restore();
}


  function calcualteFrameParts(frameWidth, stab, frameHeight) {
    const horizLines = Math.floor(frameHeight / stab) + 1;
    const vertLines = Math.floor(frameWidth / stab) + 1;
    const horizontalStripes = horizLines * Math.ceil(frameWidth / stab);
    const verticalStripes = vertLines * Math.ceil(frameHeight / stab);
    const totalAluStripes = horizontalStripes + verticalStripes;
    const angle90 = 4;
    const tConnectors = (horizLines - 2) * 2 + (vertLines - 2) * 2;
    const crosses = (horizLines - 2) * (vertLines - 2);

    setAluFrameParts({
      stripes: totalAluStripes,
      angle90,
      tConnectors,
      crosses
    });
  }
}